import { useCallback, useLayoutEffect, useRef, useState, type ReactNode, type UIEvent, type WheelEvent } from 'react'
import type { SystemFitMode } from '@/components/dataquality/personViewMemory'

const ZOOM_MIN = 0.4
const ZOOM_MAX = 3

export function systemFitAxes(
  mode: SystemFitMode,
  viewportWidth: number,
  viewportHeight: number,
  contentWidth: number,
  contentHeight: number,
  zoom: number,
): { x: number; y: number } {
  const z = zoom > 0 ? zoom : 1
  if (contentWidth <= 0 || contentHeight <= 0 || viewportWidth <= 0 || viewportHeight <= 0) {
    return { x: z, y: z }
  }
  const widthScale = viewportWidth / contentWidth
  const heightScale = viewportHeight / contentHeight
  if (mode === 'fixed-width') return { x: 1, y: z }
  if (mode === 'height') return { x: z, y: heightScale }
  if (mode === 'width') {
    const scale = widthScale * z
    return { x: scale, y: scale }
  }
  const fit = Math.min(widthScale, heightScale, 1.1) * z
  return { x: fit, y: fit }
}

interface FittedScrollPaneProps {
  resetKey: string
  zoom: number
  fitMode?: SystemFitMode
  scrollLeft: number
  scrollTop: number
  onScroll: (left: number, top: number) => void
  onWheelZoom: (zoom: number) => void
  children: ReactNode
  className?: string
}

export function FittedScrollPane({
  resetKey,
  zoom,
  fitMode = 'fit',
  scrollLeft,
  scrollTop,
  onScroll,
  onWheelZoom,
  children,
  className = '',
}: FittedScrollPaneProps) {
  const viewportRef = useRef<HTMLDivElement>(null)
  const contentRef = useRef<HTMLDivElement>(null)
  const scrollRef = useRef({ left: scrollLeft, top: scrollTop })
  const remembered = useRef({ key: resetKey, left: scrollLeft, top: scrollTop })
  if (remembered.current.key !== resetKey) {
    remembered.current = { key: resetKey, left: scrollLeft, top: scrollTop }
  }
  scrollRef.current = remembered.current
  const persistScrollRef = useRef(false)
  const prevZoomRef = useRef(zoom)
  const [box, setBox] = useState({ width: 0, height: 0, vw: 0, vh: 0 })

  const measure = useCallback(() => {
    const viewport = viewportRef.current
    const content = contentRef.current
    if (!viewport || !content) return
    const previous = content.style.transform
    content.style.transform = 'none'
    const width = Math.max(content.scrollWidth, content.offsetWidth)
    const height = Math.max(content.scrollHeight, content.offsetHeight)
    content.style.transform = previous
    const vw = viewport.clientWidth
    const vh = viewport.clientHeight
    if (width <= 0 || height <= 0 || vw <= 0 || vh <= 0) return
    setBox((prev) => (
      prev.width === width && prev.height === height && prev.vw === vw && prev.vh === vh
        ? prev
        : { width, height, vw, vh }
    ))
  }, [])

  useLayoutEffect(() => {
    measure()
    const viewport = viewportRef.current
    const content = contentRef.current
    if (typeof ResizeObserver === 'undefined' || !viewport || !content) return
    const ro = new ResizeObserver(measure)
    ro.observe(viewport)
    ro.observe(content)
    return () => ro.disconnect()
  }, [measure, resetKey])

  useLayoutEffect(() => {
    persistScrollRef.current = false
    const left = scrollRef.current.left
    const top = scrollRef.current.top
    const apply = () => {
      measure()
      const el = viewportRef.current
      if (!el) return
      el.scrollLeft = left
      el.scrollTop = top
    }
    apply()
    let armed = false
    const id = requestAnimationFrame(() => {
      armed = true
      apply()
      persistScrollRef.current = true
    })
    return () => {
      cancelAnimationFrame(id)
      if (!armed) persistScrollRef.current = true
    }
  }, [resetKey, measure])

  useLayoutEffect(() => {
    const prev = prevZoomRef.current
    prevZoomRef.current = zoom
    if (!(prev <= 1 && zoom > 1)) return
    const el = viewportRef.current
    if (!el) return
    persistScrollRef.current = false
    el.scrollLeft = remembered.current.left
    el.scrollTop = remembered.current.top
    let armed = false
    const id = requestAnimationFrame(() => {
      armed = true
      persistScrollRef.current = true
    })
    return () => {
      cancelAnimationFrame(id)
      if (!armed) persistScrollRef.current = true
    }
  }, [zoom])

  const ready = box.width > 0 && box.height > 0 && box.vw > 0 && box.vh > 0
  const axes = ready
    ? systemFitAxes(fitMode, box.vw, box.vh, box.width, box.height, zoom)
    : { x: 1, y: 1 }
  const fitLocksScroll = fitMode === 'fit' && zoom <= 1
  const overflow = !ready || fitLocksScroll
    ? 'hidden'
    : fitMode === 'width'
      ? 'hidden auto'
      : fitMode === 'height'
        ? 'auto hidden'
        : 'auto'

  const handleScroll = (event: UIEvent<HTMLDivElement>) => {
    if (!persistScrollRef.current || fitLocksScroll) return
    const target = event.currentTarget
    remembered.current = { key: resetKey, left: target.scrollLeft, top: target.scrollTop }
    scrollRef.current = remembered.current
    onScroll(target.scrollLeft, target.scrollTop)
  }

  const handleWheel = (event: WheelEvent<HTMLDivElement>) => {
    if (!event.ctrlKey && !event.metaKey) return
    event.preventDefault()
    const delta = event.deltaY < 0 ? 0.1 : -0.1
    onWheelZoom(Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, Math.round((zoom + delta) * 10) / 10)))
  }

  return (
    <div
      ref={viewportRef}
      data-testid="system-scroller"
      className={`h-full max-h-full w-full min-h-0 ${className}`}
      style={{ overflow, scrollbarGutter: 'stable' }}
      onScroll={handleScroll}
      onWheel={handleWheel}
    >
      <div
        className="relative"
        style={ready ? {
          width: Math.max(1, Math.ceil(box.width * axes.x)),
          height: Math.max(1, Math.ceil(box.height * axes.y)),
        } : undefined}
      >
        <div
          ref={contentRef}
          className="inline-block w-max min-w-max"
          style={ready ? {
            position: 'absolute',
            top: 0,
            left: 0,
            width: 'max-content',
            minWidth: 'max-content',
            transform: `scale(${axes.x}, ${axes.y})`,
            transformOrigin: 'top left',
          } : { width: 'max-content', minWidth: 'max-content' }}
        >
          {children}
        </div>
      </div>
    </div>
  )
}
